// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { catchError, filter, mergeMap, withLatestFrom } from "rxjs/operators"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { create } from "@bufbuild/protobuf"
import * as Actions from "../actions"
import * as TagsActions from "../../tags/actions"
import * as TimelineSlice from "../slices/timeline"
import { showSnackBar } from "../../snackbar/actions"
import type { RootState } from "../../../app/store"
import { Code, executorMutation, Init, isConnectError, timelineService } from "../../../app/rpc"
import { DetachTagRequestSchema, DetachTagResponse } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

export type DetachTagRpc = (request: Init<typeof DetachTagRequestSchema>) => Observable<DetachTagResponse>

export const detachTagFulfilled = (detachTagRequest: Init<typeof DetachTagRequestSchema>, state: RootState): Observable<Action> =>
  detachTagRequest.target?.case === "timeRowIds" ? detachTagFromRows(detachTagRequest) : detachTagFromGroup(detachTagRequest, state)

export const detachTag = (detachTagRpc: DetachTagRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.detachTag.match),
      withLatestFrom(state$),
      mergeMap(([{ payload: detachTagRequest }, state]) =>
        detachTagRpc(detachTagRequest)
          .pipe(
            mergeMap(_ => detachTagFulfilled(detachTagRequest, state)),
            catchError(handleError),
          )
      ),
    )

const detachTagFromRows = (request: Init<typeof DetachTagRequestSchema>): Observable<Action> =>
  concat(
    of(TimelineSlice.detachTimeRowsTag({
      timeRowIds: request.target?.case === "timeRowIds" ? request.target?.value?.ids || [] : [],
      tagId: request.tagId ?? "",
    })),
    of(TagsActions.fetchRecentTags())
  )

const detachTagFromGroup = (request: Init<typeof DetachTagRequestSchema>, state: RootState): Observable<Action> => {
  const timeGroupId = request.target?.case === "timeGroupId" ? request.target?.value : ""
  const actions: Action[] = [
    TimelineSlice.detachTimeGroupTag({
      timeGroupId,
      tagId: request.tagId ?? "",
    }),
    TagsActions.fetchRecentTags()
  ]

  // Generate timesheet field configs for detached time group if needed
  if (state?.timeline?.timeGroupsCache) {
    const { tagId } = request
    const timeGroup = state.timeline.timeGroupsCache[timeGroupId]
    if (timeGroup) {
      const updatedTimeGroup = {
        ...timeGroup,
        tags: timeGroup.tags.filter(t => t.id !== tagId) // detach tag from time group
      }

      actions.push(Actions.generateTimesheetFieldConfigs([updatedTimeGroup]))
    }
  }

  return concat(actions)
}

const handleError = (e: unknown): Observable<Action> => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      return of(showSnackBar({
        message: t({
          id: "msg_detach_tag_failed_not_found",
          message: "Failed to detach tag. Could not find the target time row or timesheet."
        }),
        messageType: "ERROR",
      }))
    }
    if (code === Code.PermissionDenied) {
      return of(showSnackBar({
        message: t({
          id: "msg_detach_tag_failed_permission_denied",
          message: "Failed to detach tag. You do not have permission to add a tag to the time row or timesheet."
        }),
        messageType: "ERROR",
      }))
    }
    if (code === Code.FailedPrecondition) {
      return concat(
        of(showSnackBar({
          message: t({
            id: "msg_detach_tag_failed_precondition",
            message: "Failed to detach tag. Time row may be out of sync."
          }),
          messageType: "ERROR"
        })),
        of(Actions.onTimelineOutOfSync())
      )
    }
  }
  return of(showSnackBar({
    message: t({
      id: "msg_detach_tag_failed",
      message: "Failed to detach tag. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com."
    }),
    messageType: "ERROR",
  }))
}

export const detachTagRpc = (request: Init<typeof DetachTagRequestSchema>): Observable<DetachTagResponse> =>
  executorMutation(() => timelineService.detachTag(create(DetachTagRequestSchema, request)))

export default detachTag(detachTagRpc)
